import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface NetworkOptions {
  reducedMotion: boolean;
  coarsePointer: boolean;
}

interface NetworkParticle {
  x: number;
  y: number;
  depth: number;
  radius: number;
  alpha: number;
  speed: number;
  drift: number;
  phase: number;
}

interface PulseBinding {
  element: SVGCircleElement;
  path: SVGPathElement;
  length: number;
  phase: number;
  speed: number;
}

interface MapPoint {
  x: number;
  y: number;
}

const MAP_WIDTH = 1672;
const MAP_HEIGHT = 941;

// Calibrated against the physical terrain features in 3d-europa-master.png.
// This is the only source of geographic placement for nodes, labels, and routes.
const NETWORK_POINTS: Record<string, MapPoint> = {
  rotterdam: { x: 590, y: 382 },
  hamburg: { x: 705, y: 350 },
  frankfurt: { x: 650, y: 425 },
  paris: { x: 500, y: 440 },
  ruhr: { x: 625, y: 405 },
  munich: { x: 700, y: 462 },
  prague: { x: 760, y: 420 },
  warsaw: { x: 905, y: 370 },
  vienna: { x: 805, y: 465 },
  bratislava: { x: 820, y: 470 },
  budapest: { x: 865, y: 490 },
  milan: { x: 700, y: 525 },
  lyon: { x: 560, y: 505 },
  ljubljana: { x: 770, y: 515 },
  zagreb: { x: 810, y: 535 },
  belgrade: { x: 895, y: 565 },
};

function buildRoutePath(from: MapPoint, to: MapPoint, curve: number): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.max(1, Math.hypot(dx, dy));
  const controlX = (from.x + to.x) / 2 - (dy / distance) * curve;
  const controlY = (from.y + to.y) / 2 + (dx / distance) * curve;
  return `M${from.x} ${from.y} Q${controlX.toFixed(2)} ${controlY.toFixed(2)} ${to.x} ${to.y}`;
}

function buildCorridorPath(): string {
  const names = ['rotterdam', 'frankfurt', 'vienna', 'budapest'];
  const curves = [6, -7, 2];
  const first = NETWORK_POINTS[names[0]];
  let path = `M${first.x} ${first.y}`;

  for (let index = 0; index < names.length - 1; index += 1) {
    const from = NETWORK_POINTS[names[index]];
    const to = NETWORK_POINTS[names[index + 1]];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.max(1, Math.hypot(dx, dy));
    const controlX = (from.x + to.x) / 2 - (dy / distance) * curves[index];
    const controlY = (from.y + to.y) / 2 + (dx / distance) * curves[index];
    path += ` Q${controlX.toFixed(2)} ${controlY.toFixed(2)} ${to.x} ${to.y}`;
  }

  return path;
}

function configureNetworkGeometry(
  section: HTMLElement,
  corridorPath: SVGPathElement,
  traveledPath: SVGPathElement,
): void {
  selectAll<SVGGElement>(section, '.network-hub[data-node]').forEach((hub) => {
    const nodeName = hub.dataset.node;
    const point = nodeName ? NETWORK_POINTS[nodeName] : undefined;
    if (point) hub.setAttribute('transform', `translate(${point.x} ${point.y})`);
  });

  selectAll<HTMLElement>(section, '.hub-label[data-node-label]').forEach((label) => {
    const nodeName = label.dataset.nodeLabel;
    const point = nodeName ? NETWORK_POINTS[nodeName] : undefined;
    if (!point) return;
    label.style.left = `${(point.x / MAP_WIDTH) * 100}%`;
    label.style.top = `${(point.y / MAP_HEIGHT) * 100}%`;
  });

  selectAll<SVGPathElement>(section, '.network-route-base .network-route').forEach((route) => {
    const fromName = route.dataset.from;
    const toName = route.dataset.to;
    const from = fromName ? NETWORK_POINTS[fromName] : undefined;
    const to = toName ? NETWORK_POINTS[toName] : undefined;
    if (!from || !to) return;
    route.setAttribute('d', buildRoutePath(from, to, Number(route.dataset.curve ?? 0)));
  });

  const corridorGeometry = buildCorridorPath();
  corridorPath.setAttribute('d', corridorGeometry);
  traveledPath.setAttribute('d', corridorGeometry);
}

const select = <T extends Element>(root: ParentNode, selector: string): T | null =>
  root.querySelector<T>(selector);

const selectAll = <T extends Element>(root: ParentNode, selector: string): T[] =>
  Array.from(root.querySelectorAll<T>(selector));

function setCountValues(section: HTMLElement, progress: number): void {
  selectAll<HTMLElement>(section, '[data-network-count]').forEach((element) => {
    const target = Number(element.dataset.networkCount ?? 0);
    const value = Math.round(target * progress);
    element.textContent = new Intl.NumberFormat('en-US').format(value);
  });
}

function setPathDraw(path: SVGPathElement, progress: number): number {
  const length = path.getTotalLength();
  path.style.strokeDasharray = `${length}`;
  path.style.strokeDashoffset = `${length * (1 - progress)}`;
  return length;
}

function placeCircleOnPath(
  circle: SVGCircleElement,
  path: SVGPathElement,
  length: number,
  progress: number,
): void {
  const point = path.getPointAtLength(Math.min(1, Math.max(0, progress)) * length);
  circle.setAttribute('cx', point.x.toFixed(2));
  circle.setAttribute('cy', point.y.toFixed(2));
}

function placeVehicleOnPath(
  vehicle: SVGGElement,
  path: SVGPathElement,
  length: number,
  progress: number,
): void {
  const distance = Math.min(1, Math.max(0, progress)) * length;
  const point = path.getPointAtLength(distance);
  const previous = path.getPointAtLength(Math.max(0, distance - 1.5));
  const next = path.getPointAtLength(Math.min(length, distance + 1.5));
  const angle = Math.atan2(next.y - previous.y, next.x - previous.x) * (180 / Math.PI);
  vehicle.setAttribute(
    'transform',
    `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)}) rotate(${angle.toFixed(2)})`,
  );
}

function initNetworkParticles(
  section: HTMLElement,
  canvas: HTMLCanvasElement,
): { setActive: (active: boolean) => void } {
  const context = canvas.getContext('2d');
  if (!context) return { setActive: () => undefined };

  let width = window.innerWidth;
  let height = window.innerHeight;
  let active = false;
  let documentVisible = !document.hidden;
  const count = window.innerWidth < 1100 ? 30 : 42;
  const particles: NetworkParticle[] = [];

  const createParticle = (): NetworkParticle => ({
    x: Math.random() * width,
    y: Math.random() * height,
    depth: 0.25 + Math.random() * 0.75,
    radius: 0.3 + Math.random() * 0.8,
    alpha: 0.025 + Math.random() * 0.095,
    speed: 0.025 + Math.random() * 0.07,
    drift: 0.1 + Math.random() * 0.25,
    phase: Math.random() * Math.PI * 2,
  });

  const resize = (): void => {
    width = section.clientWidth || window.innerWidth;
    height = window.innerHeight;
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  resize();
  for (let index = 0; index < count; index += 1) particles.push(createParticle());

  const render = (): void => {
    if (active && documentVisible) {
      context.clearRect(0, 0, width, height);
      particles.forEach((particle) => {
        particle.phase += 0.003;
        particle.y -= particle.speed * particle.depth;
        particle.x += Math.sin(particle.phase) * particle.drift * 0.035;
        if (particle.y < -3) {
          particle.y = height + 3;
          particle.x = Math.random() * width;
        }

        context.beginPath();
        context.fillStyle = `rgba(218, 232, 238, ${particle.alpha})`;
        context.arc(particle.x, particle.y, particle.radius * particle.depth, 0, Math.PI * 2);
        context.fill();
      });
    }
    requestAnimationFrame(render);
  };

  window.addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange', () => {
    documentVisible = !document.hidden;
  });
  requestAnimationFrame(render);

  return {
    setActive(nextActive: boolean): void {
      active = nextActive;
      if (!active) context.clearRect(0, 0, width, height);
    },
  };
}

function initPulseSystem(
  section: HTMLElement,
  corridorPath: SVGPathElement,
  corridorVehicle: SVGGElement,
): {
  setActive: (active: boolean) => void;
  setCorridorProgress: (progress: number) => void;
  corridorLength: number;
} {
  const bindings: PulseBinding[] = [];
  let active = false;

  selectAll<SVGCircleElement>(section, '.network-pulse:not(.corridor-pulse)').forEach(
    (element, index) => {
      const routeId = element.dataset.routeTarget;
      const path = routeId ? select<SVGPathElement>(section, `#${routeId}`) : null;
      if (!path) return;
      bindings.push({
        element,
        path,
        length: path.getTotalLength(),
        phase: (index * 0.139) % 1,
        speed: 0.000014 + (index % 4) * 0.0000035,
      });
    },
  );

  const corridorLength = corridorPath.getTotalLength();
  placeVehicleOnPath(corridorVehicle, corridorPath, corridorLength, 0);

  gsap.ticker.add(() => {
    if (!active) return;
    const now = performance.now();
    bindings.forEach((binding) => {
      const progress = (binding.phase + now * binding.speed) % 1;
      placeCircleOnPath(binding.element, binding.path, binding.length, progress);
    });
  });

  return {
    setActive(nextActive: boolean): void {
      active = nextActive;
    },
    setCorridorProgress(progress: number): void {
      placeVehicleOnPath(corridorVehicle, corridorPath, corridorLength, progress);
    },
    corridorLength,
  };
}

function initNetworkParallax(section: HTMLElement): void {
  const terrain = select<HTMLElement>(section, '#network-terrain');
  const routes = select<SVGSVGElement>(section, '#network-routes');
  const labels = select<HTMLElement>(section, '#network-label-layer');
  const particles = select<HTMLCanvasElement>(section, '#network-particle-field');
  if (!terrain || !routes || !labels || !particles) return;

  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let active = false;

  section.addEventListener('pointermove', (event) => {
    const rect = section.getBoundingClientRect();
    targetX = (event.clientX - rect.left) / Math.max(1, rect.width) - 0.5;
    targetY = event.clientY / window.innerHeight - 0.5;
  });

  section.addEventListener('pointerenter', () => {
    active = true;
  });

  section.addEventListener('pointerleave', () => {
    active = false;
    targetX = 0;
    targetY = 0;
  });

  gsap.ticker.add(() => {
    currentX += (targetX - currentX) * (active ? 0.035 : 0.055);
    currentY += (targetY - currentY) * (active ? 0.035 : 0.055);
    gsap.set(terrain, { x: currentX * 6, y: currentY * 6 });
    gsap.set(routes, { x: currentX * 10, y: currentY * 10 });
    gsap.set(labels, { x: currentX * 14, y: currentY * 14 });
    gsap.set(particles, { x: currentX * 20, y: currentY * 20 });
  });
}

export function initEuropeNetwork(options: NetworkOptions): void {
  const section = document.querySelector<HTMLElement>('#europe-in-motion');
  if (!section) return;

  const stage = select<HTMLElement>(section, '#network-stage');
  const environment = select<HTMLElement>(section, '#network-environment');
  const mapCamera = select<HTMLElement>(section, '#network-map-camera');
  const terrain = select<HTMLElement>(section, '#network-terrain');
  const terrainLight = select<HTMLElement>(section, '.network-terrain-light');
  const routesSvg = select<SVGSVGElement>(section, '#network-routes');
  const labels = select<HTMLElement>(section, '#network-label-layer');
  const particleCanvas = select<HTMLCanvasElement>(section, '#network-particle-field');
  const lightBridge = select<HTMLElement>(section, '#network-light-bridge');
  const bridgeLines = selectAll<SVGPathElement>(section, '.bridge-line');
  const bridgeBranches = selectAll<SVGPathElement>(section, '.bridge-branch');
  const intro = select<HTMLElement>(section, '#network-intro');
  const eyebrow = select<HTMLElement>(section, '.network-eyebrow');
  const titleInner = select<HTMLElement>(section, '.network-title-mask > span');
  const lede = select<HTMLElement>(section, '.network-lede');
  const microData = select<HTMLElement>(section, '#network-micro-data');
  const baseRoutes = selectAll<SVGPathElement>(section, '.network-route-base .network-route');
  const groupOne = selectAll<SVGPathElement>(section, '.route-group-1');
  const groupTwo = selectAll<SVGPathElement>(section, '.route-group-2');
  const groupThree = selectAll<SVGPathElement>(section, '.route-group-3');
  const hubs = selectAll<SVGGElement>(section, '.network-hub');
  const hubLabels = selectAll<HTMLElement>(section, '.hub-label');
  const pulseGroup = select<SVGGElement>(section, '.network-pulses');
  const corridorPath = select<SVGPathElement>(section, '#route-corridor');
  const traveledPath = select<SVGPathElement>(section, '#route-traveled');
  const corridorVehicle = select<SVGGElement>(section, '#corridor-vehicle');
  const storyLabel = select<HTMLElement>(section, '.corridor-story-label');
  const moments = selectAll<HTMLElement>(section, '.corridor-moment');
  const statement = select<HTMLElement>(section, '#network-statement');
  const statementLines = selectAll<HTMLElement>(section, '.network-statement-mask > span');
  const statusPanel = select<HTMLElement>(section, '#network-status-panel');
  const vignette = select<HTMLElement>(section, '#network-ambient-vignette');
  const exitBlackout = select<HTMLElement>(section, '#network-exit-blackout');
  const exitLight = select<HTMLElement>(section, '#network-exit-light');
  const exitLightShape = select<HTMLElement>(section, '#network-exit-light span');

  if (
    !stage || !environment || !mapCamera || !terrain || !terrainLight || !routesSvg ||
    !labels || !particleCanvas || !lightBridge || !intro || !eyebrow || !titleInner ||
    !lede || !microData || !pulseGroup || !corridorPath || !traveledPath || !corridorVehicle ||
    !storyLabel || !statement ||
    !statusPanel || !vignette || !exitBlackout || !exitLight || !exitLightShape
  ) return;

  configureNetworkGeometry(section, corridorPath, traveledPath);

  if (options.reducedMotion) {
    setCountValues(section, 1);
    return;
  }

  [...baseRoutes, corridorPath, traveledPath].forEach((path) => {
    setPathDraw(path, 0);
  });

  bridgeLines.forEach((path) => setPathDraw(path, 0.24));
  bridgeBranches.forEach((path) => setPathDraw(path, 0));

  gsap.set(stage, { visibility: 'hidden' });
  gsap.set(environment, { opacity: 1 });
  gsap.set(mapCamera, {
    opacity: 0,
    scale: 1,
    xPercent: 0,
    yPercent: 2,
    clipPath: 'inset(0 62% 0 0)',
    filter: 'brightness(0.28)',
  });
  gsap.set(terrainLight, { opacity: 0.08 });
  gsap.set(eyebrow, { opacity: 0, y: 10, letterSpacing: '0.55em' });
  gsap.set(titleInner, { yPercent: 140 });
  gsap.set(lede, { opacity: 0, y: 10 });
  gsap.set(microData, { opacity: 0, y: 8 });
  gsap.set(hubs, { opacity: 0, scale: 0.3, transformOrigin: 'center center' });
  gsap.set(hubLabels, { opacity: 0, x: 7 });
  gsap.set(pulseGroup, { opacity: 0 });
  gsap.set(corridorPath, { opacity: 0 });
  gsap.set(traveledPath, { opacity: 0 });
  gsap.set(corridorVehicle, { opacity: 0, scale: 0.72, transformOrigin: 'center center' });
  gsap.set(storyLabel, { opacity: 0, y: -7 });
  gsap.set(moments, { opacity: 0, x: 20, filter: 'blur(5px)' });
  gsap.set(statement, { opacity: 0 });
  gsap.set(statementLines, { yPercent: 140 });
  gsap.set(statusPanel, { opacity: 0, y: 14 });
  gsap.set(exitBlackout, { opacity: 0 });
  gsap.set(exitLight, { opacity: 0 });
  gsap.set(exitLightShape, { width: 6, scaleY: 1 });

  setCountValues(section, 0);

  const particleSystem = initNetworkParticles(section, particleCanvas);
  const pulseSystem = initPulseSystem(section, corridorPath, corridorVehicle);
  if (!options.coarsePointer && window.innerWidth > 760) initNetworkParallax(section);

  const corridorState = { progress: 0 };
  const counterState = { progress: 0 };
  const stationHubs = ['rotterdam', 'frankfurt', 'vienna', 'budapest'].map(
    (name) => select<SVGGElement>(section, `.network-hub[data-node="${name}"]`),
  );

  const setJourneyProgress = (progress: number): void => {
    pulseSystem.setCorridorProgress(progress);
    traveledPath.style.strokeDashoffset = `${pulseSystem.corridorLength * (1 - progress)}`;
  };

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (window.innerWidth <= 760 ? 4.4 : 5.6))}`,
      scrub: window.innerWidth <= 760 ? 0.42 : 0.65,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => {
        gsap.set(stage, { visibility: 'visible' });
        particleSystem.setActive(true);
        pulseSystem.setActive(true);
      },
      onEnterBack: () => {
        gsap.set(stage, { visibility: 'visible' });
        particleSystem.setActive(true);
        pulseSystem.setActive(true);
      },
      onLeave: () => {
        particleSystem.setActive(false);
        pulseSystem.setActive(false);
      },
      onLeaveBack: () => {
        gsap.set(stage, { visibility: 'hidden' });
        particleSystem.setActive(false);
        pulseSystem.setActive(false);
      },
      onUpdate: (self) => {
        section.dataset.storyProgress = self.progress.toFixed(4);
      },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) gsap.set(stage, { visibility: 'visible' });
      },
    },
  });

  timeline
    .to(bridgeLines, { strokeDashoffset: 0, duration: 0.11, stagger: 0.006, ease: 'power2.out' }, 0)
    .to(bridgeBranches, { strokeDashoffset: 0, duration: 0.09, stagger: 0.012, ease: 'power2.out' }, 0.065)
    .to(mapCamera, {
      opacity: 0.94,
      clipPath: 'inset(0 0% 0 0)',
      filter: 'brightness(1)',
      duration: 0.14,
      ease: 'power2.out',
    }, 0.015)
    .to(terrainLight, { opacity: 0.36, duration: 0.12 }, 0.035)
    .to(lightBridge, { opacity: 0, duration: 0.035 }, 0.11)
    .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '0.35em', duration: 0.055, ease: 'power3.out' }, 0.09)
    .to(titleInner, { yPercent: 0, duration: 0.085, ease: 'power3.out' }, 0.105)
    .to(lede, { opacity: 1, y: 0, duration: 0.06, ease: 'power3.out' }, 0.145)
    .to(groupOne, { strokeDashoffset: 0, duration: 0.13, stagger: 0.007, ease: 'power2.out' }, 0.11)
    .to(groupTwo, { strokeDashoffset: 0, duration: 0.14, stagger: 0.008, ease: 'power2.out' }, 0.17)
    .to(groupThree, { strokeDashoffset: 0, duration: 0.14, stagger: 0.008, ease: 'power2.out' }, 0.225)
    .to(hubs, { opacity: 1, scale: 1, duration: 0.075, stagger: 0.012, ease: 'power3.out' }, 0.17)
    .to(hubLabels, { opacity: 0.74, x: 0, duration: 0.065, stagger: 0.01, ease: 'power3.out' }, 0.22)
    .to(pulseGroup, { opacity: 0.82, duration: 0.07 }, 0.25)
    .to(microData, { opacity: 1, y: 0, duration: 0.07, ease: 'power3.out' }, 0.245)
    .to(counterState, {
      progress: 1,
      duration: 0.11,
      ease: 'power2.out',
      onUpdate: () => setCountValues(section, counterState.progress),
    }, 0.22)
    .to(mapCamera, { scale: 1.1, xPercent: -3, yPercent: 0, duration: 0.16 }, 0.13)
    .to(intro, { opacity: 0, x: -28, y: -18, duration: 0.06 }, 0.24)
    .to(microData, { opacity: 0.28, x: -14, duration: 0.06 }, 0.245)
    .to(baseRoutes, { opacity: 0.12, duration: 0.05 }, 0.245)
    .to(hubLabels, { opacity: 0.3, duration: 0.05 }, 0.245)
    .to(corridorPath, { opacity: 0.9, strokeDashoffset: 0, duration: 0.075, ease: 'power2.out' }, 0.255)
    .to(traveledPath, { opacity: 1, duration: 0.035 }, 0.285)
    .to(corridorVehicle, { opacity: 1, scale: 1, duration: 0.045, ease: 'power3.out' }, 0.29)
    .to(storyLabel, { opacity: 1, y: 0, duration: 0.045 }, 0.29)
    .to(mapCamera, { scale: 1.18, xPercent: -7.5, yPercent: -1.5, duration: 0.14 }, 0.25)

    // Station 01 — Rotterdam. Arrival and a real reading pause.
    .to(corridorState, {
      progress: 0.02,
      duration: 0.025,
      ease: 'power2.out',
      onUpdate: () => setJourneyProgress(corridorState.progress),
    }, 0.305)
    .to(mapCamera, { scale: 1.2, xPercent: -3.5, yPercent: -1.2, duration: 0.045 }, 0.305)
    .to(stationHubs[0], { opacity: 1, scale: 1.42, filter: 'drop-shadow(0 0 7px rgba(225,240,246,.72))', duration: 0.018 }, 0.32)
    .to(moments[0], { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.04, ease: 'power3.out' }, 0.32)
    .to(moments[0], { opacity: 1, duration: 0.07 }, 0.36)
    .to(moments[0], { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.025 }, 0.43)
    .to(stationHubs[0], { opacity: 0.72, scale: 1, filter: 'none', duration: 0.018 }, 0.442)

    // Leg 01 and Station 02 — Frankfurt.
    .to(corridorState, {
      progress: 0.34,
      duration: 0.075,
      ease: 'power2.inOut',
      onUpdate: () => setJourneyProgress(corridorState.progress),
    }, 0.425)
    .to(mapCamera, { scale: 1.22, xPercent: -6.2, yPercent: -1.8, duration: 0.055 }, 0.435)
    .to(stationHubs[1], { opacity: 1, scale: 1.42, filter: 'drop-shadow(0 0 7px rgba(225,240,246,.72))', duration: 0.018 }, 0.48)
    .to(moments[1], { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.035, ease: 'power3.out' }, 0.475)
    .to(moments[1], { opacity: 1, duration: 0.065 }, 0.51)
    .to(moments[1], { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.025 }, 0.575)
    .to(stationHubs[1], { opacity: 0.72, scale: 1, filter: 'none', duration: 0.018 }, 0.587)

    // Leg 02 and Station 03 — Vienna.
    .to(corridorState, {
      progress: 0.76,
      duration: 0.085,
      ease: 'power2.inOut',
      onUpdate: () => setJourneyProgress(corridorState.progress),
    }, 0.565)
    .to(mapCamera, { scale: 1.24, xPercent: -10, yPercent: -2, duration: 0.065 }, 0.565)
    .to(stationHubs[2], { opacity: 1, scale: 1.42, filter: 'drop-shadow(0 0 7px rgba(225,240,246,.72))', duration: 0.018 }, 0.625)
    .to(moments[2], { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.035, ease: 'power3.out' }, 0.62)
    .to(moments[2], { opacity: 1, duration: 0.06 }, 0.655)
    .to(moments[2], { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.025 }, 0.715)
    .to(stationHubs[2], { opacity: 0.72, scale: 1, filter: 'none', duration: 0.018 }, 0.727)

    // Leg 03 and Station 04 — Budapest. The traveled line is now complete.
    .to(corridorState, {
      progress: 1,
      duration: 0.065,
      ease: 'power2.inOut',
      onUpdate: () => setJourneyProgress(corridorState.progress),
    }, 0.705)
    .to(mapCamera, { scale: 1.25, xPercent: -12.5, yPercent: -2.2, duration: 0.055 }, 0.71)
    .to(stationHubs[3], { opacity: 1, scale: 1.42, filter: 'drop-shadow(0 0 7px rgba(225,240,246,.72))', duration: 0.018 }, 0.755)
    .to(moments[3], { opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.035, ease: 'power3.out' }, 0.75)
    .to(moments[3], { opacity: 1, duration: 0.055 }, 0.785)
    .to(moments[3], { opacity: 0, x: -12, filter: 'blur(3px)', duration: 0.025 }, 0.84)
    .to(stationHubs[3], { opacity: 0.72, scale: 1, filter: 'none', duration: 0.018 }, 0.852)

    // Continental scale reveal.
    .to(storyLabel, { opacity: 0, y: -5, duration: 0.03 }, 0.84)
    .to(corridorVehicle, { opacity: 0, scale: 0.72, duration: 0.03 }, 0.845)
    .to(mapCamera, { scale: 1.06, xPercent: -2, yPercent: 0, duration: 0.075, ease: 'power2.inOut' }, 0.84)
    .to(baseRoutes, { opacity: 0.72, duration: 0.06 }, 0.84)
    .to(hubLabels, { opacity: 0.68, duration: 0.05 }, 0.84)
    .to(microData, { opacity: 0.72, x: 0, duration: 0.045 }, 0.845)
    .to(statement, { opacity: 1, duration: 0.025 }, 0.855)
    .to(statementLines, { yPercent: 0, duration: 0.045, stagger: 0.008, ease: 'power3.out' }, 0.855)
    .fromTo(
      select<HTMLElement>(section, '.network-statement p'),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.035, ease: 'power3.out' },
      0.88,
    )
    .to(statusPanel, { opacity: 1, y: 0, duration: 0.04, ease: 'power3.out' }, 0.87)
    .to(vignette, { opacity: 0.78, duration: 0.04 }, 0.86)

    // Precision-light handoff for Section 03.
    .to([statement, statusPanel, microData], { opacity: 0, y: -10, duration: 0.035 }, 0.925)
    .to([baseRoutes, hubs, hubLabels, pulseGroup, corridorPath, traveledPath], { opacity: 0.035, duration: 0.04 }, 0.925)
    .to(mapCamera, { scale: 1.32, xPercent: -15, yPercent: -5, opacity: 0.08, filter: 'brightness(0.35)', duration: 0.075 }, 0.925)
    .to(exitBlackout, { opacity: 1, duration: 0.065 }, 0.93)
    .to(exitLight, { opacity: 1, duration: 0.025 }, 0.955)
    .to(exitLightShape, { width: '76vw', scaleY: 0.82, duration: 0.045, ease: 'power2.inOut' }, 0.955);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
